import { useState, FC } from 'react';
import { Button, Checkbox, Form, Tabs, Typography } from 'antd';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

import { HeaderButton } from 'componets/ui';
import { TextField, FormItem, SubmitButton } from 'componets/form';
import { ModalComponent } from 'componets/common';
import { LoginLayout } from 'componets/layouts';

import { useAppSelector, useTranslate } from 'hooks';

import { register } from 'features/registration/actions';

import { Rules, RequestStatus, TARGET_URL } from 'utils/constants';
import Utils from 'utils/utils';
import { getInStorage, removeFromStorage } from 'utils';

import { ModalProps, FieldData } from 'models';

import { AppDispatch } from 'store';

import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';

import styles from '../index.module.scss';

const MIN_PASSWORD_LENGTH = 8;

export const CreateAccount: FC = () => {
	const { alreadyHaveAccountText, privateText, companyText, signUpText, loginText } = useTranslate([
		'alreadyHaveAccountText',
		'privateText',
		'companyText',
		'signUpText',
		'loginText',
	]);

	const [fields, setFields] = useState<FieldData[]>([]);

	const [type, setType] = useState('user');
	const [isNotifyNewslettersOpen, setIsNotifyNewslettersOpen] = useState(false);
	const [notifyNewsletters, setNotifyNewsletters] = useState('1');

	const dispatch = useDispatch<AppDispatch>();

	const onFinish = async (data: any) => {
		const values = {
			...data,
			userType: type,
			notifyNewsletters,
			affiliateInfo: Cookies.get(TARGET_URL),
		};

		return dispatch(register(values))
			.unwrap()
			.then(() => {
				removeFromStorage('referralEmail');
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);
			});
	};

	const formProps = {
		onFinish,
		fields,
		onFieldsChange: setFields,
		initialValues: {
			email:
				typeof getInStorage('referralEmail') === 'string'
					? JSON.parse(getInStorage('referralEmail') || '')
					: '',
		},
	};

	return (
		<LoginLayout top={<HeaderButton text={alreadyHaveAccountText} link="/" title={loginText} />}>
			<Typography.Title level={4}>{signUpText}</Typography.Title>

			<Tabs
				type="card"
				className={styles.forms}
				defaultActiveKey={type}
				onChange={(v) => setType(v)}
			>
				<Tabs.TabPane tab={privateText} key="user">
					<CreateAccountForm {...formProps} />
				</Tabs.TabPane>

				<Tabs.TabPane tab={companyText} key="company">
					<CreateAccountForm {...formProps} />
				</Tabs.TabPane>
			</Tabs>

			<Typography.Text type="secondary" className={styles.news}>
				{notifyNewsletters !== '0' && (
					<Trans
						i18nKey="receiveInfoText"
						components={{
							button: <Button type="text" onClick={() => setIsNotifyNewslettersOpen(true)} />,
						}}
					/>
				)}
			</Typography.Text>

			{isNotifyNewslettersOpen && (
				<NotifyNewslettersModal
					isOpen={isNotifyNewslettersOpen}
					onClose={() => setIsNotifyNewslettersOpen(false)}
					onCancel={() => {
						setNotifyNewsletters('0');
						setIsNotifyNewslettersOpen(false);
					}}
				/>
			)}
		</LoginLayout>
	);
};

type CreateAccountFormProps = {
	onFinish: (values: any) => void;
	fields: FieldData[];
	onFieldsChange: ((changedFields: FieldData[], allFields: FieldData[]) => void) | undefined;
	initialValues: { [key: string]: string | null };
};

const CreateAccountForm: FC<CreateAccountFormProps> = ({
	onFinish,
	fields,
	onFieldsChange,
	initialValues,
}) => {
	const loading = useAppSelector((state) => state.registration.status);

	const { emailText, passwordText, createAccountText } = useTranslate([
		'emailText',
		'passwordText',
		'createAccountText',
	]);
	return (
		<Form
			onFinish={onFinish}
			onFieldsChange={onFieldsChange}
			fields={fields}
			initialValues={initialValues}
		>
			<FormItem validateTrigger="onSubmit" rules={[Rules.REQUIRED, Rules.EMAIL]} name="email">
				<TextField label={emailText} />
			</FormItem>

			<FormItem
				className={`${styles.password} password`}
				rules={[Rules.REQUIRED, Rules.PASSWORD]}
				name="password"
			>
				<TextField label={passwordText} type="password" />
			</FormItem>
			<FormItem name="password" className="passwordIncludes">
				<PasswordIncludes />
			</FormItem>
			<FormItem
				className="checkbox"
				name="agreeWithPolicyAndTerms"
				valuePropName="checked"
				rules={[Rules.REQUIRED_CHECKBOX]}
			>
				<Checkbox>
					<Trans
						i18nKey="confirmPrivacyText"
						components={{
							privacy_link_lt: (
								<a
									href="https://crowdpear.com/wp-content/uploads/2024/04/Privatumo_politika.pdf"
									target="_blank"
									rel="noreferrer"
								/>
							),
							privacy_link_en: (
								<a
									href="https://crowdpear.com/wp-content/uploads/2024/04/Privacy_policy.pdf"
									target="_blank"
									rel="noreferrer"
								/>
							),
							term_use_lt: (
								<a
									href="https://crowdpear.com/wp-content/uploads/2022/10/Sutelktinio_finansavimo_platformos_crowdpear_naudojimosi_sutartis.pdf"
									target="_blank"
									rel="noreferrer"
								/>
							),
							term_use_en: (
								<a
									href="https://crowdpear.com/wp-content/uploads/2022/10/Usage_agreement_of_the_crowdfunding_platform_Crowdpear.pdf"
									target="_blank"
									rel="noreferrer"
								/>
							),
						}}
					/>
				</Checkbox>
			</FormItem>

			<SubmitButton text={createAccountText} loading={loading === RequestStatus.PENDING} />
		</Form>
	);
};

type PasswordIncludesProps = {
	value?: string;
};

const PASSWORD_INCLUDES = [
	{
		text: 'passwordMessageOneText',
		rule: (v: string | undefined) => v && /[a-z]/g.test(v),
	},
	{
		text: 'passwordMessageTwoText',
		rule: (v: string | undefined) => v && v.length >= MIN_PASSWORD_LENGTH,
	},
	{
		text: 'passwordMessageThreeText',
		rule: (v: string | undefined) => v && /[A-Z]/g.test(v),
	},
	{
		text: 'passwordMessageFourText',
		rule: (v: string | undefined) => v && /[0-9]/g.test(v),
	},
];

const PasswordIncludes: FC<PasswordIncludesProps> = ({ value }) => {
	const { t } = useTranslation();
	return (
		<div>
			<Typography.Text className={styles.includesTitle}>{t('passwordIncludeText')}</Typography.Text>
			{PASSWORD_INCLUDES.map((item) => {
				const isValidRule = item.rule(value);

				return (
					<Typography.Text
						className={styles.includes}
						type={isValidRule ? 'success' : 'secondary'}
						key={item.text}
					>
						<CheckIcon
							className={classnames(styles.icon, {
								[styles.iconSuccess]: isValidRule,
							})}
						/>
						{t(item.text)}
					</Typography.Text>
				);
			})}
		</div>
	);
};

interface NotifyNewslettersModalProps extends ModalProps {
	onCancel: () => void;
}

const NotifyNewslettersModal: FC<NotifyNewslettersModalProps> = ({ isOpen, onClose, onCancel }) => {
	const { receiveInfoCancelText, yesText, cancelText } = useTranslate([
		'receiveInfoCancelText',
		'yesText',
		'cancelText',
	]);

	return (
		<ModalComponent isOpen={isOpen} onClose={onClose}>
			<div className="modal-content">
				<Typography.Text>{receiveInfoCancelText}</Typography.Text>
				<div className="modal-footer">
					<Button onClick={onCancel} type="primary">
						{yesText}
					</Button>
					<Button onClick={onClose}>{cancelText}</Button>
				</div>
			</div>
		</ModalComponent>
	);
};
